import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import apiClient from "api/axiosConfig";
import Spinner from "components/Common/Spinner";
import FormSelectField from "./components/FormSelectField";
import LazyLoadSelectField from "./components/LazyLoadSelectField";

const difficultyOptions = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
];

let cachedCategories = null;

const PackagedTemplateForm = ({
  formData,
  errors,
  backendError,
  isSubmitting,
  handleChange,
  handleCategoryChange,
  handleSingleSelectChange,
  handleMappingChange,
  addMapping,
  removeMapping,
  handleSubmit,
}) => {
  const { name, description, difficulty, categories, language_mappings, image_path } = formData;
  const imageFileName = image_path?.name || "";

  const [categoriesList, setCategoriesList] = useState(() => cachedCategories || []);

  useEffect(() => {
    if (cachedCategories) return;
    let isMounted = true;
    apiClient
      .get("packaged-templates/categories")
      .then((res) => {
        if (!isMounted) return;
        const catsData = res.data?.data || res.data || [];
        const formatted = catsData.map((item) => ({
          value: Number(item.id !== undefined ? item.id : item.value),
          label: item.name || item.title || item.label,
        }));
        cachedCategories = formatted;
        setCategoriesList(formatted);
      })
      .catch((err) => console.error("Error fetching categories:", err));

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <>
      <Spinner isActive={isSubmitting} text="Saving Packaged Template..." />
      <div className="container bg-white rounded shadow-sm p-4">
        <h4 className="mb-4 text-primary border-bottom pb-2">Create Packaged Template</h4>

        <form onSubmit={handleSubmit} noValidate className="needs-validation">
          {backendError && (
            <div className="alert alert-danger mb-4" role="alert">
              {backendError}
            </div>
          )}

          <div className="row mb-3">
            <div className="col-md-4">
              <label htmlFor="templateName" className="form-label">
                Template Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="templateName"
                name="name"
                value={name}
                onChange={handleChange}
                placeholder="e.g., Office 365 Security Alert"
                style={{ height: "38px", minHeight: "38px" }}
                className={`form-control ${errors.name ? "is-invalid" : ""}`}
              />
              {errors.name && <div className="invalid-feedback text-start d-block">{errors.name}</div>}
            </div>

            <FormSelectField
              label="Difficulty"
              name="difficulty"
              value={difficulty}
              options={difficultyOptions}
              isRequired
              error={errors.difficulty}
              onChange={handleSingleSelectChange}
              colClass="col-md-4"
            />

            <FormSelectField
              label="Categories"
              name="categories"
              value={categories}
              options={categoriesList}
              isMulti
              isRequired
              error={errors.categories}
              onChange={handleCategoryChange}
              colClass="col-md-4"
            />
          </div>

          <div className="row mb-3">
            <div className="col-md-12">
              <label htmlFor="templateDescription" className="form-label">Description</label>
              <textarea
                id="templateDescription"
                name="description"
                value={description}
                onChange={handleChange}
                rows="3"
                className="form-control"
                style={{ resize: "none" }}
                placeholder="Brief description of the template..."
              />
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-12 mb-3">
              <label className="form-label">Thumbnail Image (Max 2MB)</label>
              <div className="file-upload-box">
                <input
                  type="file"
                  id="imagePathFile"
                  name="image_path"
                  onChange={handleChange}
                  className="file-input"
                  accept=".jpeg,.jpg,.png,.gif"
                />
                <label htmlFor="imagePathFile" className="file-upload-label">
                  {imageFileName || "Click to upload thumbnail."}
                </label>
              </div>
              {errors.image_path && (
                <div className="invalid-feedback d-block text-start">{errors.image_path}</div>
              )}
            </div>
          </div>

          <div className="card mb-4 border bg-light">
            <div className="card-body">
              <h6 className="card-title text-secondary mb-3">System Allocation</h6>
              <div className="mb-2">
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    id="modeGlobal"
                    name="tenant_mode"
                    value="global"
                    checked={true}
                    readOnly
                    onChange={() => {}}
                  />
                  <label className="form-check-label" htmlFor="modeGlobal">Global (All Active Tenants)</label>
                </div>
                {/* 
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    id="modeSpecific"
                    name="tenant_mode"
                    value="specific"
                    checked={tenant_mode === "specific"}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="modeSpecific">Specific Tenant</label>
                </div> 
                */}
              </div>
              {/* 
              {tenant_mode === "specific" && (
                <div className="mt-3">
                  <label htmlFor="targetTenantId" className="form-label">
                    Venza Tenant ID <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    id="targetTenantId"
                    name="target_tenant_id"
                    value={target_tenant_id}
                    onChange={handleChange}
                    className={`form-control ${errors.target_tenant_id ? "is-invalid" : ""}`}
                    placeholder="Enter Tenant ID..."
                  />
                  {errors.target_tenant_id && (
                    <div className="invalid-feedback text-start d-block">{errors.target_tenant_id}</div>
                  )}
                </div>
              )} 
              */}
            </div>
          </div>

          <div className="card mb-4 border bg-light">
            <div className="card-header bg-light d-flex justify-content-between align-items-center py-3">
              <h6 className="mb-0 text-secondary">
                Language Mappings <span className="text-danger">*</span>
              </h6>
              <button
                type="button"
                className="btn btn-sm btn-outline-success"
                onClick={addMapping}
              >
                + Add Mapping
              </button>
            </div>

            <div className="card-body pb-4">
              {errors.language_mappings && (
                <div className="alert alert-danger p-2 small mb-3">{errors.language_mappings}</div>
              )}

              {language_mappings.length === 0 ? (
                <p className="text-muted text-center my-3">No mappings added. Click "+ Add Mapping" to start.</p>
              ) : (
                language_mappings.map((mapping, index) => {
                  const hasMappingErr = !!errors.language_mappings;
                  return (
                    <div
                      key={index}
                      className="border rounded p-3 mb-4 bg-white shadow-sm position-relative"
                      style={{ paddingBottom: "1.75rem" }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="badge bg-secondary">Mapping - {index + 1}</span>
                        {language_mappings.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeMapping(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div 
                        style={{ 
                          display: "grid", 
                          gridTemplateColumns: "1.2fr 2.5fr 2.5fr 2.3fr 1.5fr", 
                          gap: "0.5rem",
                          width: "100%" 
                        }}
                      >
                        <LazyLoadSelectField
                          label="Language"
                          value={mapping.language_id}
                          endpoint="languages"
                          onChange={(val) => handleMappingChange(index, "language_id", val)}
                          isRequired
                          hasError={hasMappingErr && !mapping.language_id}
                        />

                        <LazyLoadSelectField
                          label="Email Template"
                          value={mapping.email_template_id}
                          endpoint="gophish/templates"
                          onChange={(val) => handleMappingChange(index, "email_template_id", val)}
                          isRequired
                          hasError={hasMappingErr && !mapping.email_template_id}
                        />

                        <LazyLoadSelectField
                          label="Landing Page"
                          value={mapping.landing_page_id}
                          endpoint="gophish/pages"
                          onChange={(val) => handleMappingChange(index, "landing_page_id", val)}
                          isRequired
                          hasError={hasMappingErr && !mapping.landing_page_id}
                        />

                        <LazyLoadSelectField
                          label="Sending Profile"
                          value={mapping.sending_profile_id}
                          endpoint="gophish/sending-profiles"
                          onChange={(val) => handleMappingChange(index, "sending_profile_id", val)}
                          isRequired
                          hasError={hasMappingErr && !mapping.sending_profile_id}
                        />

                        <LazyLoadSelectField
                          label="Phishing Domain"
                          value={mapping.phishing_domain_id}
                          endpoint="gophish/domains"
                          onChange={(val) => handleMappingChange(index, "phishing_domain_id", val)}
                          isRequired
                          hasError={hasMappingErr && !mapping.phishing_domain_id}
                        />
                      </div>
                    </div>
                  );
                })
              )}

              <div 
                className="alert shadow-sm d-flex align-items-center my-4 py-2 px-3"
                style={{ border: "2px dark #007bff", backgroundColor: "#e9f5ff" }}
              >
                <small className="mb-0 text-dark">
                  <strong>NOTE:</strong> Please ensure each selected language is mapped with the corresponding set of Email Template and Landing Page.
                </small>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-end mb-2">
            <button type="submit" className="btn btn-primary px-4 py-2" disabled={isSubmitting}>
              Submit Packaged Template
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

PackagedTemplateForm.propTypes = {
  formData: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  backendError: PropTypes.string,
  isSubmitting: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleCategoryChange: PropTypes.func.isRequired,
  handleSingleSelectChange: PropTypes.func.isRequired,
  handleMappingChange: PropTypes.func.isRequired,
  addMapping: PropTypes.func.isRequired,
  removeMapping: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
};

export default PackagedTemplateForm;